import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as adminUserService from "../services/adminUserService";
import * as adminRoleService from "../services/adminRoleService";
import type { User, Role } from "../types/api";
import type { AdminCreateUserPayload } from "../services/adminUserService";
import { ClientBotsModal } from "../components/admin/ClientBotsModal";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { useToast } from "../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DataTable,
  Pagination,
  type DataTableColumn,
} from "../components/table/DataTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { cn } from "../lib/utils";

const PAGE_SIZE = 8;

const adminFieldClass =
  "bg-background text-foreground placeholder:text-muted-foreground border border-input dark:bg-white/5 dark:text-white dark:placeholder:text-white/40";
const adminSelectTriggerClass =
  "bg-background text-foreground border-input dark:bg-white/5 dark:text-white";

const AdminUsersPage = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [followupFilter, setFollowupFilter] = useState<
    User["followupStatus"] | "all"
  >("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [managerOptions, setManagerOptions] = useState<User[]>([]);
  const isManager =
    currentUser?.role === "manager_contact" ||
    currentUser?.role === "manager_trader";
  const rolesForForm = useMemo(
    () =>
      (isManager ? roles.filter((r) => r.name === "client") : roles).filter(
        (r) => r.name !== "superadmin"
      ),
    [roles, isManager]
  );
  const [form, setForm] = useState<
    AdminCreateUserPayload & { tradingBotAllowed?: boolean }
  >({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    role: "",
    status: "active",
    managerId: null,
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [botsModalUser, setBotsModalUser] = useState<User | null>(null);

  const getEffectiveFollowupStatus = (u: User): User["followupStatus"] => {
    const derived =
      (u.followupStatus ??
        (u.contactStatus === "contacted" ? "no_response" : "new")) ||
      "new";
    return derived;
  };

  const filteredUsers = useMemo(() => {
    if (followupFilter === "all") {
      return users;
    }
    return users.filter(
      (u) =>
        u.role === "client" && getEffectiveFollowupStatus(u) === followupFilter
    );
  }, [users, followupFilter]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const latestRequestRef = useRef<number | null>(null);
  const requestCounterRef = useRef(0);

  const fetchUsers = useCallback(
    async (
      page: number,
      search: string,
      activeFollowupFilter: typeof followupFilter
    ) => {
      const requestId = ++requestCounterRef.current;
      latestRequestRef.current = requestId;
      setIsTableLoading(true);
      try {
        const effectiveLimit = activeFollowupFilter === "all" ? PAGE_SIZE : 100;
        const { users: data, meta } = await adminUserService.listUsers({
          page,
          limit: effectiveLimit,
          search: search.trim() ? search.trim() : undefined,
        });
        if (latestRequestRef.current !== requestId) {
          return;
        }
        // Défense UI: ne jamais afficher superadmin dans la grille utilisateurs.
        setUsers(data.filter((u) => u.role !== "superadmin"));
        if (activeFollowupFilter === "all") {
          const normalizedPage = Number(meta?.page ?? page);
          const normalizedLimit = Number(meta?.limit ?? PAGE_SIZE);
          const normalizedTotal = Number(meta?.total ?? data?.length ?? 0);
          const normalizedTotalPages = Number(
            meta?.totalPages ??
              Math.max(1, Math.ceil(normalizedTotal / normalizedLimit))
          );
          setPagination({
            page: normalizedPage,
            limit: normalizedLimit,
            total: normalizedTotal,
            totalPages: normalizedTotalPages,
          });
        } else {
          // Quand un filtre de statut est actif, on charge un gros bloc
          // et on ramène la pagination à une seule page basée sur le filtrage local.
          setPagination({
            page: 1,
            limit: effectiveLimit,
            total: data.filter((u) => u.role !== "superadmin").length,
            totalPages: 1,
          });
        }
      } catch (err) {
        if (latestRequestRef.current !== requestId) {
          return;
        }
        toast({
          title: t("errors.generic"),
          description:
            (err as string) ??
            t("admin.users.errors.loadUsers"),
          variant: "destructive",
        });
      } finally {
        if (latestRequestRef.current === requestId) {
          setIsTableLoading(false);
          setIsInitialLoading(false);
        }
      }
    },
    [toast, PAGE_SIZE, t]
  );

  const fetchRoles = useCallback(async () => {
    try {
      const data = await adminRoleService.listRoles();
      setRoles(data);
      // Définir le premier rôle par défaut si disponible
      if (data.length > 0) {
        setForm((prev) => {
          if (!prev.role) {
            return { ...prev, role: data[0].name };
          }
          return prev;
        });
      }
    } catch (err) {
      toast({
        title: t("errors.generic"),
        description:
          (err as string) ??
          t("admin.users.errors.loadRoles"),
        variant: "destructive",
      });
    } finally {
      setIsLoadingRoles(false);
    }
  }, [toast, t]);

  const fetchManagers = useCallback(async () => {
    try {
      const [{ users: contacts }, { users: traders }] = await Promise.all([
        adminUserService.listUsers({
          role: "manager_contact",
          limit: 100,
        }),
        adminUserService.listUsers({
          role: "manager_trader",
          limit: 100,
        }),
      ]);
      setManagerOptions([...contacts, ...traders]);
    } catch (err) {
      toast({
        title: t("errors.generic"),
        description:
          (err as string) ??
          t(
            "admin.users.errors.loadManagers",
            "Impossible de charger la liste des managers disponibles."
          ),
        variant: "destructive",
      });
    }
  }, [toast, t]);

  useEffect(() => {
    void fetchUsers(currentPage, searchQuery, followupFilter);
  }, [fetchUsers, currentPage, searchQuery, followupFilter]);

  useEffect(() => {
    void fetchRoles();
    void fetchManagers();
  }, [fetchRoles, fetchManagers]);

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    if (name === "managerId") {
      setForm((prev) => ({
        ...prev,
        managerId: value ? Number(value) : null,
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetForm = useCallback(
    (user?: User | null) => {
      if (user) {
        setForm({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone ?? "",
          password: "",
          role: user.role,
          status: user.status,
          managerId: user.managerId,
        });
      } else {
        setForm({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          password: "",
          role: isManager ? "client" : roles.length > 0 ? roles[0].name : "",
          status: "active",
          managerId: null,
        });
      }
    },
    [roles, isManager]
  );

  useEffect(() => {
    if (form.role !== "client" && form.managerId !== null) {
      setForm((prev) => ({ ...prev, managerId: null }));
      return;
    }
    if (
      form.role === "client" &&
      !form.managerId &&
      managerOptions.length > 0
    ) {
      setForm((prev) => ({ ...prev, managerId: managerOptions[0].id }));
    }
  }, [form.role, form.managerId, managerOptions]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      if (form.role === "client" && !form.managerId) {
        throw new Error(
          t(
            "admin.users.errors.selectManagerForClient",
            "Veuillez selectionner un manager pour ce client."
          )
        );
      }
      const payload: AdminCreateUserPayload = {
        ...form,
        managerId: form.role === "client" ? form.managerId : null,
      };
      if (editingUser) {
        const updatePayload: Parameters<typeof adminUserService.updateUser>[1] =
          {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phone,
            role: form.role,
            status: form.status,
            managerId: payload.managerId,
            notifyEmail: false,
            notifySms: false,
            tradingBotAllowed:
              form.role === "client" ? form.tradingBotAllowed : undefined,
          };
        if (form.password.trim()) {
          updatePayload.password = form.password;
        }
        const updated = await adminUserService.updateUser(
          editingUser.id,
          updatePayload
        );
        setUsers((prev) =>
          prev.map((u) => (u.id === editingUser.id ? updated : u))
        );
        toast({
          title: t("admin.users.toasts.userUpdatedTitle"),
          description: t(
            "admin.users.toasts.userUpdatedDescription",
            "{{firstName}} {{lastName}} a ete modifie.",
            { firstName: updated.firstName, lastName: updated.lastName }
          ),
          variant: "success",
        });
        if (
          editingUser.role !== updated.role &&
          (editingUser.role === "manager_contact" ||
            editingUser.role === "manager_trader" ||
            updated.role === "manager_contact" ||
            updated.role === "manager_trader")
        ) {
          void fetchManagers();
        }
      } else {
        const created = await adminUserService.createUser(payload);
        setUsers((prev) =>
          created ? [created, ...prev].slice(0, PAGE_SIZE) : prev
        );
        toast({
          title: t("admin.users.toasts.userCreatedTitle"),
          description: t(
            "admin.users.toasts.userCreatedDescription",
            "{{firstName}} {{lastName}} a ete ajoute.",
            { firstName: created.firstName, lastName: created.lastName }
          ),
          variant: "success",
        });
        setCurrentPage(1);
        await fetchUsers(1, searchQuery, followupFilter);
        if (
          created.role === "manager_contact" ||
          created.role === "manager_trader"
        ) {
          void fetchManagers();
        }
      }
      setIsCreateModalOpen(false);
    } catch (err) {
      toast({
        title: t("errors.generic"),
        description:
          (err as string) ??
          t("admin.users.errors.saveUser"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setEditingUser(null);
      resetForm();
    }
  };

  useEffect(() => {
    if (!isCreateModalOpen) {
      resetForm();
      setIsSubmitting(false);
      setEditingUser(null);
    } else if (editingUser) {
      resetForm(editingUser);
    } else {
      resetForm();
    }
  }, [editingUser, isCreateModalOpen, resetForm]);

  const handleRoleToggle = useCallback(
    async (targetUser: User, nextRole: User["role"]) => {
      try {
        const previousRole = targetUser.role;
        const updated = await adminUserService.updateUser(targetUser.id, {
          role: nextRole,
        });
        setUsers((prev) =>
          prev.map((u) => (u.id === targetUser.id ? updated : u))
        );
        toast({
          title: t("admin.users.toasts.roleUpdatedTitle"),
          description: t(
            "admin.users.toasts.roleUpdatedDescription",
            "{{firstName}} est maintenant {{role}}.",
            { firstName: updated.firstName, role: updated.role }
          ),
          variant: "success",
        });
        const wasManager =
          previousRole === "manager_contact" ||
          previousRole === "manager_trader";
        const isManagerNow =
          nextRole === "manager_contact" || nextRole === "manager_trader";
        if (wasManager || isManagerNow) {
          void fetchManagers();
        }
      } catch (err) {
        toast({
          title: t("errors.generic"),
          description:
            (err as string) ??
            t("admin.users.errors.updateRole"),
          variant: "destructive",
        });
      }
    },
    [fetchManagers, toast]
  );

  const handleManagerAssign = useCallback(
    async (targetUser: User, managerId: number | null) => {
      try {
        const updated = await adminUserService.updateUser(targetUser.id, {
          managerId,
        });
        setUsers((prev) =>
          prev.map((u) => (u.id === targetUser.id ? updated : u))
        );
        toast({
          title: t("admin.users.toasts.managerUpdatedTitle"),
          description: `${updated.firstName} est maintenant rattaché à ${
            managerId
              ? `${
                  managerOptions.find((m) => m.id === managerId)?.firstName ??
                  "son manager"
                }`
              : "aucun manager"
          }.`,
          variant: "success",
        });
      } catch (err) {
        toast({
          title: t("errors.generic"),
          description:
            (err as string) ??
            t(
              "admin.users.errors.updateManager",
              "Impossible de mettre a jour le manager."
            ),
          variant: "destructive",
        });
      }
    },
    [managerOptions, toast, t]
  );

  const FOLLOWUP_OPTIONS: {
    value: User["followupStatus"];
    labelKey: string;
    colorClass: string;
  }[] = [
    {
      value: "new",
      labelKey: "admin.users.followup.new",
      colorClass: "text-sky-800 dark:text-sky-300",
    },
    {
      value: "no_response",
      labelKey: "admin.users.followup.noResponse",
      colorClass: "text-amber-900 dark:text-amber-300",
    },
    {
      value: "voicemail",
      labelKey: "admin.users.followup.voicemail",
      colorClass: "text-blue-800 dark:text-blue-300",
    },
    {
      value: "not_interested",
      labelKey: "admin.users.followup.notInterested",
      colorClass: "text-rose-800 dark:text-rose-300",
    },
    {
      value: "deposit_done",
      labelKey: "admin.users.followup.depositDone",
      colorClass: "text-emerald-800 dark:text-emerald-300",
    },
    {
      value: "potential_low",
      labelKey: "admin.users.followup.potentialLow",
      colorClass: "text-orange-900 dark:text-orange-300",
    },
  ];

  const userColumns = useMemo<DataTableColumn<User>[]>(
    () => [
      {
        key: "name",
        header: t("admin.users.table.name"),
        sortable: true,
        sortKey: "name",
        sortDirection: "asc",
        className: "font-medium",
        cell: (user) => (
          <button
            type="button"
            className="text-left text-foreground hover:text-cyan-600 underline-offset-2 hover:underline dark:text-white dark:hover:text-cyan-400"
            onClick={() => {
              setEditingUser(user);
              resetForm(user);
              setIsCreateModalOpen(true);
            }}>
            {user.firstName} {user.lastName}
          </button>
        ),
      },
      {
        key: "email",
        header: t("admin.users.table.email"),
        cell: (user) => user.email,
      },
      {
        key: "phone",
        header: t("admin.users.table.phone"),
        cell: (user) => user.phone ?? "—",
      },
      {
        key: "role",
        header: t("admin.users.table.role"),
        cell: (user) =>
          isLoadingRoles ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <Select
              value={user.role}
              onValueChange={(value: string) =>
                handleRoleToggle(user, value as User["role"])
              }>
              <SelectTrigger className="h-8 w-32 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rolesForForm.map((role) => (
                  <SelectItem key={role.id} value={role.name}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
      },
      {
        key: "manager",
        header: t("admin.users.table.manager"),
        cell: (user) =>
          user.role === "client" ? (
            managerOptions.length ? (
              <Select
                value={user.managerId ? String(user.managerId) : ""}
                onValueChange={(value: string) =>
                  handleManagerAssign(user, value ? Number(value) : null)
                }>
                <SelectTrigger className="h-8 w-40 text-xs">
                  <SelectValue placeholder={t("admin.users.form.select")} />
                </SelectTrigger>
                <SelectContent>
                  {managerOptions.map((manager) => (
                    <SelectItem key={manager.id} value={String(manager.id)}>
                      {manager.firstName} {manager.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs text-muted-foreground dark:text-white/60">
                {t("admin.users.noManagerAvailable")}
              </span>
            )
          ) : (
            <span className="text-muted-foreground dark:text-white/60">—</span>
          ),
      },
      {
        key: "status",
        header: t("admin.users.table.status"),
        cell: (user) => <span className="capitalize">{user.status}</span>,
      },
      {
        key: "followup",
        header: t("admin.users.followup.columnHeader"),
        cell: (user) =>
          user.role === "client" ? (
            <div className="flex flex-col text-xs">
              {(() => {
                const current =
                  (user.followupStatus ??
                    (user.contactStatus === "contacted"
                      ? "no_response"
                      : "new")) ||
                  "new";
                const currentOpt =
                  FOLLOWUP_OPTIONS.find((opt) => opt.value === current) ??
                  FOLLOWUP_OPTIONS[0];
                const baseTriggerClasses =
                  "h-8 w-44 bg-background text-xs border border-input dark:bg-white/5 dark:border-white/20";
                const colorClass = currentOpt.colorClass;
                return (
                  <Select
                    value={current}
                    onValueChange={async (value: string) => {
                      const next = value as User["followupStatus"];
                      try {
                        const updated = await adminUserService.updateUser(
                          user.id,
                          {
                            followupStatus: next,
                          }
                        );
                        setUsers((prev) =>
                          prev.map((u) => (u.id === user.id ? updated : u))
                        );
                      } catch (err) {
                        toast({
                          title: t("errors.generic"),
                          description:
                            (err as string) ??
                            t(
                              "admin.users.errors.updateFollowupStatus",
                              "Impossible de mettre a jour le statut de suivi."
                            ),
                          variant: "destructive",
                        });
                      }
                    }}>
                    <SelectTrigger
                      className={cn(baseTriggerClasses, colorClass)}>
                      <SelectValue
                        placeholder={t("admin.users.followup.new")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOWUP_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt.value ?? "none"}
                          value={opt.value ?? "new"}
                          className={opt.colorClass}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
              {user.hasPendingManagerPayment && (
                <span className="mt-1 text-amber-900 dark:text-amber-300">
                  🔔{" "}
                  {t(
                    "admin.users.followup.pendingPayment",
                    "Paiement en attente de validation"
                  )}
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground dark:text-white/60">
              —
            </span>
          ),
      },
      {
        key: "createdAt",
        header: t("admin.users.table.createdAt"),
        cell: (user) => new Date(user.createdAt).toLocaleDateString(),
      },
      {
        key: "robots",
        header: t("admin.users.table.robots"),
        cell: (user) =>
          user.role === "client" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setBotsModalUser(user)}>
              {t("tradingBot.admin.manageBots")}
            </Button>
          ) : (
            <span className="text-muted-foreground dark:text-white/60">—</span>
          ),
      },
    ],
    [
      handleManagerAssign,
      handleRoleToggle,
      isLoadingRoles,
      managerOptions,
      resetForm,
      rolesForForm,
      FOLLOWUP_OPTIONS,
      t,
      toast,
    ]
  );

  if (isInitialLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-3xl border border-border bg-muted/30 p-6 dark:border-white/10 dark:bg-black/20">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="mt-4 h-32 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground dark:text-white">
          {t("admin.sidebarUsers")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t(
            "admin.users.pageSubtitle",
            "Ajoutez des profils, leurs roles et surveillez l'activite en temps reel."
          )}
        </p>
      </div>
      <div className="flex flex-col gap-4 rounded-3xl border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-white/10 dark:bg-white/5">
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Label className="text-xs uppercase text-muted-foreground dark:text-white/60">
              {t("admin.users.searchLabel")}
            </Label>
            <Input
              placeholder={t("admin.users.searchPlaceholder")}
              value={searchQuery}
              onChange={(event) => handleSearchChange(event.target.value)}
              className={adminFieldClass}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex flex-col gap-1 sm:w-64">
              <Label className="text-xs uppercase text-muted-foreground dark:text-white/60">
                {t(
                  "admin.users.followup.filterLabel",
                  "Filtrer par statut client"
                )}
              </Label>
              <Select
                value={followupFilter ?? "all"}
                onValueChange={(value: string) =>
                  setFollowupFilter(
                    value === "all" ? "all" : (value as User["followupStatus"])
                  )
                }>
                <SelectTrigger
                  className={cn(
                    "h-9 text-xs sm:h-10",
                    adminSelectTriggerClass
                  )}>
                  <SelectValue
                    placeholder={t(
                      "admin.users.followup.filterAll",
                      "Tous les statuts"
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("admin.users.followup.filterAll")}
                  </SelectItem>
                  {FOLLOWUP_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value ?? "none"}
                      value={opt.value ?? "new"}
                      className={opt.colorClass}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 text-xs text-muted-foreground sm:items-end sm:text-sm dark:text-white/70">
          <span>
            {filteredUsers.length} {t("admin.users.result")}
            {filteredUsers.length > 1 && "s"}
          </span>
          <Button
            type="button"
            className="w-full sm:w-auto"
            onClick={() => setIsCreateModalOpen(true)}>
            {t("admin.users.addUserButton")}
          </Button>
        </div>
      </div>

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="w-full max-w-4xl border-none bg-transparent p-0 text-foreground dark:text-white">
          <div className="rounded-3xl border border-border bg-card p-6 shadow-2xl sm:p-8 dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-900/80 dark:to-slate-800/60">
            <DialogHeader className="mb-6 text-left">
              <DialogTitle className="text-2xl font-semibold text-foreground dark:text-white">
                {editingUser
                  ? t("admin.users.editUserTitle")
                  : t("admin.users.addUserTitle")}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground dark:text-white/60">
                {t(
                  "admin.users.formDescription",
                  "Tous les comptes crees ici recoivent un e-mail d'activation immediat."
                )}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("admin.users.form.firstName")}</Label>
                  <Input
                    name="firstName"
                    value={form.firstName}
                    onChange={handleFormChange}
                    required
                    className={adminFieldClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.users.form.lastName")}</Label>
                  <Input
                    name="lastName"
                    value={form.lastName}
                    onChange={handleFormChange}
                    required
                    className={adminFieldClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.users.form.email")}</Label>
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleFormChange}
                    required
                    className={adminFieldClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.users.form.phone")}</Label>
                  <Input
                    name="phone"
                    value={form.phone}
                    onChange={handleFormChange}
                    required
                    className={adminFieldClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("admin.users.form.password")}
                    {editingUser && (
                      <span className="ml-2 text-xs text-muted-foreground dark:text-white/50">
                        (
                        {t(
                          "admin.users.passwordOptional",
                          "laisser vide pour ne pas modifier"
                        )}
                        )
                      </span>
                    )}
                  </Label>
                  <Input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleFormChange}
                    required={!editingUser}
                    placeholder={
                      editingUser
                        ? t(
                            "admin.users.passwordPlaceholder",
                            "Laisser vide pour ne pas modifier"
                          )
                        : undefined
                    }
                    className={adminFieldClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.users.form.role")}</Label>
                  {isLoadingRoles ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select
                      value={form.role}
                      onValueChange={(value: string) =>
                        setForm((prev) => ({ ...prev, role: value }))
                      }>
                      <SelectTrigger className={adminSelectTriggerClass}>
                        <SelectValue
                          placeholder={t("admin.users.form.selectRole")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {rolesForForm.map((role) => (
                          <SelectItem key={role.id} value={role.name}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.users.form.status")}</Label>
                  <Select
                    value={form.status}
                    onValueChange={(value: string) =>
                      setForm((prev) => ({
                        ...prev,
                        status: value as User["status"],
                      }))
                    }>
                    <SelectTrigger className={adminSelectTriggerClass}>
                      <SelectValue
                        placeholder={t("admin.users.form.selectStatus")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        {t("admin.users.form.statusActive")}
                      </SelectItem>
                      <SelectItem value="suspended">
                        {t("admin.users.form.statusSuspended")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.role === "client" && (
                  <>
                    <div className="space-y-2 md:col-span-2">
                      <Label>{t("admin.users.form.assignedManager")}</Label>
                      {managerOptions.length ? (
                        <Select
                          value={form.managerId ? String(form.managerId) : ""}
                          onValueChange={(value: string) =>
                            setForm((prev) => ({
                              ...prev,
                              managerId: value ? Number(value) : null,
                            }))
                          }>
                          <SelectTrigger className={adminSelectTriggerClass}>
                            <SelectValue
                              placeholder={t(
                                "admin.users.form.selectManager",
                                "Selectionnez un manager"
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {managerOptions.map((manager) => (
                              <SelectItem
                                key={manager.id}
                                value={String(manager.id)}>
                                {manager.firstName} {manager.lastName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm text-amber-900 dark:text-amber-300">
                          {t(
                            "admin.users.form.noManagerHint",
                            "Aucun manager disponible. Creez d'abord un utilisateur avec le role Manager."
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 md:col-span-2">
                      <input
                        type="checkbox"
                        id="tradingBotAllowed"
                        checked={form.tradingBotAllowed ?? false}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            tradingBotAllowed: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-input bg-background dark:border-white/20 dark:bg-white/5"
                      />
                      <Label
                        htmlFor="tradingBotAllowed"
                        className="text-sm text-foreground dark:text-white/90">
                        {t(
                          "admin.users.tradingBotAllowed",
                          "Autoriser l'accès au Robot (voir l'onglet et activer les robots)"
                        )}
                      </Label>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full border border-border bg-muted text-foreground hover:bg-muted/80 sm:w-auto dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                  onClick={() => setIsCreateModalOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-cyan-500 text-slate-900 hover:bg-cyan-400 sm:w-auto">
                  {isSubmitting
                    ? editingUser
                      ? t("admin.users.form.updating")
                      : t("admin.users.form.creating")
                    : editingUser
                    ? t("admin.users.form.update")
                    : t("admin.users.form.createUser")}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <ClientBotsModal
        client={
          botsModalUser
            ? {
                id: botsModalUser.id,
                firstName: botsModalUser.firstName,
                lastName: botsModalUser.lastName,
              }
            : null
        }
        open={!!botsModalUser}
        onOpenChange={(open: boolean) => !open && setBotsModalUser(null)}
      />

      <DataTable
        data={filteredUsers}
        columns={userColumns}
        rowKey={(user) => user.id}
        emptyState={t("admin.users.emptyState")}
        isLoading={isTableLoading}
        loadingText={t("admin.users.loadingText")}
      />
      {pagination.totalPages > 1 && (
        <div className="mt-4 border-t border-border pt-3 dark:border-white/10">
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={setCurrentPage}
            isLoading={isTableLoading}
            ariaLabelPrevious={t("admin.pagination.previous")}
            ariaLabelNext={t("admin.pagination.next")}
          />
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
